import { useTranslation } from 'react-i18next'
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react'
import {
  conditionOpsFor,
  SHOW_IF_LIMITS,
  type ConditionOp,
  type ConditionValue,
  type QuestionCondition,
  type ShowIf,
  type SurveyQuestionInput,
} from 'shared'
import { Button, Field, Input, Select } from '../ui'
import { cx, focusRing } from '../../lib/utils'

/** A starting condition for a source question: valid as far as it can be before the author chooses. */
export function defaultCondition(source: SurveyQuestionInput): QuestionCondition {
  const op = conditionOpsFor(source.type)[0] ?? 'answered'
  return { question: source.code ?? '', op, ...defaultValue(source, op) }
}

function defaultValue(source: SurveyQuestionInput, op: ConditionOp): { value?: ConditionValue } {
  if (op === 'answered' || op === 'not_answered') return {}
  if (op === 'in' || op === 'not_in') return { value: [] }
  if (source.type === 'boolean') return { value: true }
  if (source.type === 'number' || source.type === 'scale') return { value: source.config?.min ?? 0 }
  return { value: source.config?.options?.[0]?.value ?? '' }
}

/**
 * Edits when a question (or a whole section) is shown. A rule can only look at an
 * earlier question that has a code, because the code is what survives copying the
 * survey, and only back, so two rules can never wait on each other.
 */
export default function ConditionBuilder({
  index,
  questions,
  onChange,
}: {
  index: number
  questions: SurveyQuestionInput[]
  onChange: (showIf: ShowIf | undefined) => void
}) {
  const { t } = useTranslation()
  const showIf = questions[index].config?.showIf
  const sources = questions.slice(0, index).filter(
    (question) => question.code && conditionOpsFor(question.type).length > 0,
  )
  const conditions = showIf?.conditions ?? []
  const conditional = showIf !== undefined

  const set = (next: QuestionCondition[], match = showIf?.match) =>
    onChange({ ...(match ? { match } : {}), conditions: next })
  const update = (at: number, patch: QuestionCondition) =>
    set(conditions.map((condition, i) => (i === at ? patch : condition)))

  const toggle = (value: string) => {
    if (value === 'always') return onChange(undefined)
    onChange({ conditions: sources[0] ? [defaultCondition(sources[0])] : [] })
  }

  return (
    <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-900/40" data-conditions>
      <Field label={t('surveys.condition.title')}>
        <Select
          value={conditional ? 'conditional' : 'always'}
          onChange={(event) => toggle(event.target.value)}
          options={[
            { value: 'always', label: t('surveys.condition.always') },
            { value: 'conditional', label: t('surveys.condition.onlyIf') },
          ]}
        />
      </Field>

      {conditional && sources.length === 0 && (
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('surveys.condition.noSource')}</p>
      )}

      {conditional && sources.length > 0 && (
        <div className="mt-3 space-y-3">
          {conditions.map((condition, at) => {
            const source = sources.find((candidate) => candidate.code === condition.question)
            const ops = source ? conditionOpsFor(source.type) : []
            return (
              <div key={at} className="space-y-2" data-condition>
                <div className="grid gap-2 sm:grid-cols-[1.4fr_1fr_auto]">
                  <Select
                    aria-label={t('surveys.condition.source')}
                    value={source ? (source.code ?? '') : ''}
                    onChange={(event) => {
                      const next = sources.find((candidate) => candidate.code === event.target.value)
                      if (next) update(at, defaultCondition(next))
                    }}
                    options={[
                      ...(source ? [] : [{ value: '', label: condition.question || '—' }]),
                      ...sources.map((candidate) => ({
                        value: candidate.code ?? '',
                        label: `${candidate.code} · ${candidate.label}`,
                      })),
                    ]}
                  />
                  <Select
                    aria-label={t('surveys.condition.operator')}
                    value={condition.op}
                    onChange={(event) => {
                      const op = event.target.value as ConditionOp
                      update(at, { question: condition.question, op, ...(source ? defaultValue(source, op) : {}) })
                    }}
                    options={ops.map((op) => ({ value: op, label: t(`surveys.condition.op_${op}`) }))}
                  />
                  <button
                    type="button"
                    aria-label={t('surveys.condition.remove')}
                    onClick={() => (conditions.length > 1 ? set(conditions.filter((_, i) => i !== at)) : onChange(undefined))}
                    className={cx(
                      'self-center rounded-md p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10',
                      focusRing,
                    )}
                  >
                    <RiDeleteBinLine className="size-4" aria-hidden="true" />
                  </button>
                </div>
                {source && (
                  <ValueInput
                    source={source}
                    condition={condition}
                    onChange={(value) => update(at, { ...condition, value })}
                  />
                )}
              </div>
            )
          })}

          {conditions.length > 1 && (
            <Field label={t('surveys.condition.match')}>
              <Select
                value={showIf?.match ?? 'all'}
                onChange={(event) => set(conditions, event.target.value as 'all' | 'any')}
                options={[
                  { value: 'all', label: t('surveys.condition.matchAll') },
                  { value: 'any', label: t('surveys.condition.matchAny') },
                ]}
              />
            </Field>
          )}

          <Button
            variant="secondary"
            onClick={() => set([...conditions, defaultCondition(sources[0])])}
            disabled={conditions.length >= SHOW_IF_LIMITS.maxConditions}
          >
            <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
            {t('surveys.condition.add')}
          </Button>
        </div>
      )}
    </div>
  )
}

function ValueInput({
  source,
  condition,
  onChange,
}: {
  source: SurveyQuestionInput
  condition: QuestionCondition
  onChange: (value: ConditionValue) => void
}) {
  const { t } = useTranslation()
  const { op, value } = condition
  const options = source.config?.options ?? []

  if (op === 'answered' || op === 'not_answered') return null

  if (op === 'in' || op === 'not_in') {
    const picked = Array.isArray(value) ? value : []
    return (
      <div role="group" aria-label={t('surveys.condition.value')} className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={picked.includes(option.value)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...picked, option.value]
                    : picked.filter((entry) => entry !== option.value),
                )
              }
              className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
            />
            {option.label}
          </label>
        ))}
        {options.length === 0 && (
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('surveys.condition.noOptions')}</span>
        )}
      </div>
    )
  }

  if (source.type === 'boolean') {
    return (
      <Select
        aria-label={t('surveys.condition.value')}
        value={value === false ? 'no' : 'yes'}
        onChange={(event) => onChange(event.target.value === 'yes')}
        options={[
          { value: 'yes', label: t('common.yes') },
          { value: 'no', label: t('common.no') },
        ]}
      />
    )
  }

  if (source.type === 'number' || source.type === 'scale') {
    return (
      <Input
        type="number"
        aria-label={t('surveys.condition.value')}
        value={typeof value === 'number' ? String(value) : ''}
        onChange={(event) => onChange(event.target.value === '' ? 0 : Number(event.target.value))}
      />
    )
  }

  return (
    <Select
      aria-label={t('surveys.condition.value')}
      value={typeof value === 'string' ? value : ''}
      onChange={(event) => onChange(event.target.value)}
      options={options.map((option) => ({ value: option.value, label: option.label }))}
    />
  )
}
